import { FinanceRate } from '@kanda-libs/ks-frontend-services';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';
import { filterForPrimaryRates, groupByAprBNPL } from 'utils';
import {
  FinanceCalculatorFormValues,
  useFinanceCalculatorContext,
} from '../../context';

import {
  calculateValuesForMatchedRates,
  filterForAprMatch,
  filterUniqueDurations,
} from './helpers';

export interface CalculationsValues {
  term: string;
  total: number;
  amount: number;
  valid: boolean;
  rate: FinanceRate;
}

export interface CalculationsHook {
  isLoading: boolean;
  values: CalculationsValues[];
  type: FinanceCalculatorFormValues['type'];
  label: string;
  show: boolean;
}

export default function useCalculations(): CalculationsHook {
  const { isLoading, rates: allRates, stored } = useFinanceCalculatorContext();

  const apr = useMemo(
    () => (stored.apr ? String(stored.apr) : undefined),
    [stored],
  );
  const type = useMemo(() => stored.type, [stored]);
  const amountToPayMonthly = useMemo(() => stored.amountToPayMonthly, [stored]);
  const amountToBorrow = useMemo(() => stored.amountToBorrow, [stored]);

  const rates = useMemo(() => {
    if (!allRates || !apr) return undefined;
    const groupedByApr = groupByAprBNPL(allRates);
    return groupedByApr?.[apr];
  }, [allRates, apr]);

  const values = useMemo(() => {
    if (isLoading || !apr || !rates) return [];
    const amount = amountToPayMonthly || amountToBorrow || 0;
    return pipe(
      rates,
      filterForPrimaryRates,
      (primary) => filterForAprMatch(primary, apr),
      filterUniqueDurations,
      (matched) => calculateValuesForMatchedRates(matched, type, amount),
    );
  }, [isLoading, rates, apr, amountToPayMonthly, amountToBorrow, type]);

  const label = useMemo(
    () => (type === 'monthlyPayments' ? 'Monthly repayment' : 'Borrow amount'),
    [type],
  );

  const show = useMemo(() => {
    if (type === 'monthlyPayments') return Boolean(amountToBorrow);
    return Boolean(amountToPayMonthly);
  }, [type, amountToBorrow, amountToPayMonthly]);

  return {
    isLoading,
    values,
    type,
    label,
    show,
  };
}
